// Sub-Step 3.3 — OidcAuthProvider für public/-Stack.
//
// Wraps react-oidc-context's AuthProvider mit ClientOnly-Boundary —
// Vike-SSR-Tree darf react-oidc-context nicht durchlaufen
// (localStorage/sessionStorage-Zugriff crashed Server-Render).
// Siehe public/src/components/common/ClientOnly.tsx.
//
// Verwendet von public/pages/+Layout.tsx (Root-Wrapper). Alle Pages
// laufen unter diesem Provider; Components können via `useAuth()` aus
// react-oidc-context (oder usePublicAuth-Wrapper) den Auth-State lesen.
//
// 2026-05-28: AutoSilentSso-Component entfernt (signinSilent() bei Mount).
// Bene-iPhone-Befund: iOS Safari zeigte "Erweiterte Datenschutzmaßnahmen"-
// Banner beim Reload. Trigger war der Silent-SSO-iframe zu
// auth.scrolly.com (cross-subdomain). Erst-Visit: keine Cookies bei
// auth → kein ITP-Block. Reload: Cookies vom Initial-iframe da →
// Safari blockt Cross-Site-Cookies → "Privacy Report"-Banner.
//
// 2026-05-28 (later): SsoHandoffReceiver v1 — speicherte den per
// postMessage empfangenen userString direkt in sessionStorage + reload.
// Revertiert (4ff204d): iframe-Pfad loopte (onLoad→send→reload→onLoad).
//
// 2026-06-10 (B5, Launch-Batch-2): SsoHandoffReceiver v2 — HINT-Modell.
// Das Portal signalisiert nur noch „Keycloak-Session existiert"; der
// Receiver macht daraufhin EINMAL pro Tab einen top-level
// signinRedirect (prompt=none) mit dem EIGENEN Client scrolly-public.
// Kein fremdes Token mehr im Storage (5-min-Lifespan + Client-Mismatch
// beim Renew), kein reload-on-message (= der Loop-Motor von v1).
// Guards: Origin-Allowlist, iframe-Detection, once-Flag (sessionStorage,
// per Tab), nur-wenn-anonym. Sender: portal/src/lib/sso-handoff.ts.
// Direkt-Links (Bookmark, Suchmaschine) bleiben anonym.
// Begründung + Entscheidungslogik: src/lib/auth/sso-hint.ts.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { AuthProvider, useAuth } from "react-oidc-context";
import ClientOnly from "../common/ClientOnly";
import { getOidcConfig } from "../../lib/auth/oidc-config";
import {
  SSO_HANDOFF_MESSAGE_TYPE,
  SSO_ACK_MESSAGE_TYPE,
  SSO_ATTEMPTED_KEY,
  SSO_RETURN_TO_KEY,
  decideSsoHint,
} from "../../lib/auth/sso-hint";

interface Props {
  children: ReactNode;
}

/**
 * Allowlist für origin-Validierung im SsoHandoffReceiver.
 * Nur diese Origins dürfen via postMessage Auth-State setzen.
 * Bei neuen Konsumenten-Domains hier ergänzen.
 *
 * Hardening 2026-05-28: localhost-Origins nur im Dev-Build aufnehmen.
 * In Production-Builds (import.meta.env.DEV === false) wird der
 * Conditional-Spread leer — Vite tree-shaked die Strings raus, so dass
 * der Prod-Bundle keine localhost-Origins akzeptiert.
 *
 * Hintergrund: eine maliziöse Seite auf localhost im selben Browser-
 * Session als der Prod-Viewer könnte sonst einen gefälschten Auth-
 * Handoff posten und sich als authentifizierter User ausgeben.
 * Attack-Surface ist hoch (User muss in einer Session malicious-
 * localhost UND prod-viewer offen haben), aber Defense-in-Depth.
 */
const ALLOWED_SSO_ORIGINS: string[] = [
  ...(import.meta.env.VITE_ALLOWED_SSO_ORIGINS || "https://portal.scrolly.com,https://my.scrolly.com")
    .split(",")
    .map((s: string) => s.trim())
    .filter(Boolean),
  // Dev: portal läuft typisch auf 5173, public auf 5176.
  // In Prod-Builds wird das Array via Vite-Tree-Shake leer.
  ...(import.meta.env.DEV
    ? ["http://localhost:5173", "http://localhost:5176"]
    : []),
];

/**
 * Empfängt den SSO-HINT von portal-Tabs (v2 — kein Token-Transfer).
 *
 * Message-Format (sync mit portal/src/lib/sso-handoff.ts):
 *   { type: "scrolly-sso-handoff", payload: { mode: "hint" } }
 *
 * Antwortet mit Ack damit der Sender sein Retry-Polling stoppt:
 *   { type: "scrolly-sso-ack" }
 *
 * Bei "redirect"-Entscheidung (s. decideSsoHint): EINMAL pro Tab ein
 * top-level signinRedirect(prompt=none) mit scrolly-public. Existiert
 * die Keycloak-SSO-Session (Portal-Login), kommt der User ohne Form
 * eingeloggt zurück (return_to via url_state); existiert sie nicht,
 * fängt oidc-callback den login_required-Fehler ab und kehrt still
 * anonym zur Scrolly-URL zurück (SSO_RETURN_TO_KEY-Fallback).
 */
function SsoHandoffReceiver() {
  const auth = useAuth();
  const [pendingHint, setPendingHint] = useState(false);
  const actedRef = useRef(false);

  // Listener: Origin validieren, Ack senden, Hint vormerken.
  useEffect(() => {
    const handler = (ev: MessageEvent) => {
      if (!ALLOWED_SSO_ORIGINS.includes(ev.origin)) return; // ignore: kein Ack an Fremde
      if ((ev.data as { type?: string } | undefined)?.type !== SSO_HANDOFF_MESSAGE_TYPE) {
        return;
      }

      // Ack IMMER an erlaubte Sender — auch wenn kein Redirect folgt
      // (eingeloggt / attempted / iframe). Stoppt das Polling drüben.
      if (ev.source && "postMessage" in ev.source) {
        (ev.source as Window).postMessage(
          { type: SSO_ACK_MESSAGE_TYPE },
          { targetOrigin: ev.origin },
        );
      }
      setPendingHint(true);
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // Verarbeitung: erst entscheiden, wenn der Auth-State geladen ist.
  useEffect(() => {
    if (!pendingHint || actedRef.current) return;
    if (auth.isLoading || auth.activeNavigator) return;

    const decision = decideSsoHint({
      originAllowed: true, // bereits im Listener geprüft
      isTopWindow: window.self === window.top,
      attempted: safeGetSessionItem(SSO_ATTEMPTED_KEY) === "1",
      isAuthenticated: auth.isAuthenticated,
    });

    actedRef.current = true;
    if (decision !== "redirect") return;

    try {
      // Flags VOR dem Redirect — once-Semantik hält auch bei Fehlschlag
      // oder Browser-Back. sessionStorage ist per-Tab und überlebt den
      // Redirect-Roundtrip (view → auth → view).
      sessionStorage.setItem(SSO_ATTEMPTED_KEY, "1");
      sessionStorage.setItem(SSO_RETURN_TO_KEY, window.location.href);
    } catch {
      // Storage gesperrt (Private-Mode-Edge) → lieber anonym bleiben
      // als ohne once-Guard redirecten.
      return;
    }

    void auth.signinRedirect({
      prompt: "none",
      url_state: JSON.stringify({ return_to: window.location.href }),
    });
  }, [pendingHint, auth, auth.isLoading, auth.activeNavigator, auth.isAuthenticated]);

  return null;
}

/** sessionStorage.getItem ohne Throw (Private-Mode/Storage-Block-Edge). */
function safeGetSessionItem(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Root-Wrapper-Component für alle Vike-Pages.
 *
 * Verwendung in public/pages/+Layout.tsx (oder einer äquivalenten
 * Root-Komponente). Wenn die SSR-Phase läuft, rendert ClientOnly
 * fallback (null) — public/ Pages funktionieren weiter, nur ohne
 * Auth-Hook bis Hydration. Nach Hydration mountet AuthProvider.
 */
export default function OidcAuthProvider({ children }: Props) {
  return (
    <ClientOnly fallback={children}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </ClientOnly>
  );
}

/**
 * Inner-Component: AuthProvider muss innerhalb ClientOnly leben weil
 * getOidcConfig() window.location.origin liest (server-side undefined).
 */
function AuthProviderInner({ children }: Props) {
  const config = getOidcConfig();
  return (
    <AuthProvider {...config}>
      <SsoHandoffReceiver />
      {children}
    </AuthProvider>
  );
}
